import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import { InspectorHeader } from '../InspectorHeader/InspectorHeader';
import IconTextSquare from '@/assets/icons/icon-text-square.svg?react';
import { Event } from '@/components/dropdowns/Event/Event';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import NodeTextAreaSettings from '@/components/inspector-fields/TextAreaField/NodeTextAreaSettings';
import NodeGlobalStyleSettings from '@/components/inspector-fields/HeadingField/NodeGlobalStyleSettings';
import { useNode } from '@craftjs/core';
import type { StylingProps, Color, RGBA } from '@builder4/shared/types/layout';
import { TextButton } from '@/components/buttons/TextButton/TextButton';
import { AlignField } from '@/components/inspector-fields/AlignField/AlignField';
import { SliderField } from '@/components/inspector-fields/SliderField/SliderField';
import NodeFontFamilySettings from '@/components/dropdowns/SelectField/NodeFontFamilySettings';
import NodeFontWeightSettings from '@/components/dropdowns/SelectField/NodeFontWeightSettings';
import NodeTextSizeSettings from '@/components/inspector-fields/SliderField/NodeTextSizeSettings';
import NodeSpaceSettings from '@/components/inspector-fields/SliderField/NodeSpaceSettings';
import NodeColorField from '@/components/inspector-fields/ColorField/NodeColorField';
import IconTextAllCaps from '@/assets/icons/icon-text-all-caps.svg?react';
import IconTextBold from '@/assets/icons/icon-text-bold.svg?react';
import IconTextItalicSlash from '@/assets/icons/icon-text-italic-slash.svg?react';
import IconTextItalic from '@/assets/icons/icon-text-italic.svg?react';
import IconTextStrikethrough from '@/assets/icons/icon-text-strikethrough.svg?react';
import IconTextUnderline from '@/assets/icons/icon-text-underline.svg?react';
import {
  applyMark,
  applyMarksToAllPreservingOverrides,
  getCurrentMarkValue,
} from '@/utils/textUtils';
import { useFonts } from '@/context/FontProvider';

import { Editor as SlateEditor, Transforms, Text } from 'slate';
import { editorRegistry } from '@/components/userComponents/Text';
import { useAppSelector } from '@/store/hooks';
import IconLink from '@/assets/icons/icon-link-01.svg?react';
import IconUnlink from '@/assets/icons/icon-unlink-01.svg?react';
import { selectActiveGlobalStyle } from '@/store/globalStyles/selectors';
import { selectBreakpoint } from '@/store/system/selectors';

import type { BodyTypography } from '@builder4/shared/types/styles';
import type { TextProps } from '@builder4/shared/components/Text';
import type { CustomText, SlateEditorType } from '@builder4/shared/types/text';
import { useSlateSelectionChange } from '@/hooks/useSlateSelectionChange';
import { LayoutElementV2 } from '@/components/inspector-fields/LayoutElement/LayoutElementV2';
import NodeAnimationSettings from '@/components/inspector-fields/AnimationSettings/NodeAnimationSettings';
interface InspectorTextProps {
  className: string;
  text: string;
}

export const InspectorText: React.FC<InspectorTextProps> = ({ className, text }) => {
  const {
    actions: { setProp },
    appliedGlobalStyle,
    textAlign,
    lineHeight,
    selectedStyleVariant,
    id,
  } = useNode((node) => ({
    appliedGlobalStyle: node.data.props.appliedGlobalStyle,
    textAlign: node.data.props.textAlign,
    lineHeight: node.data.props.lineHeight,
    selectedStyleVariant: node.data.props.selectedStyleVariant,
    id: node.id,
  }));

  const { fonts } = useFonts();
  const activeGlobalStyle = useAppSelector(selectActiveGlobalStyle);
  const breakpoint = useAppSelector(selectBreakpoint);

  // State to track current selection marks
  const [, setCurrentMarks] = useState({});
  const [selectedFontFamily, setSelectedFontFamily] = useState<{
    family: string;
    variants: string[];
  }>();
  const onHandleChangeFamily = (family: string) => {
    const selected = fonts.find((font) => font.family === family);
    setSelectedFontFamily(selected);
  };

  const handleSelectionChange = useCallback(
    ({ editorId, editor }: { editorId: string; editor: SlateEditorType }) => {
      if (editorId === id) {
        const marks = SlateEditor.marks(editor);
        setCurrentMarks(marks || {});
        const currentFontFamily = marks?.fontFamily as string;
        if (currentFontFamily && fonts.length > 0) {
          const selected = fonts.find((font) => font.family === currentFontFamily);
          setSelectedFontFamily(selected);
        }
      }
    },
    [id, fonts],
  );

  useSlateSelectionChange(handleSelectionChange);

  const handleOnToggleGlobalStyle = () => {
    const slateEditor = editorRegistry.get(id);
    if (!slateEditor) return;
    const { editor } = slateEditor;

    if (appliedGlobalStyle) {
      //todo check undo redo issue here. need to merge props changes with textStyle changes
      const [category, subVariant] = (selectedStyleVariant || '').split('.') as [
        'heading' | 'body' | 'link',
        string,
      ];
      if (category === 'heading') {
        const headingTag = subVariant as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
        const headingData = activeGlobalStyle.typography.heading[headingTag];

        if (headingData) {
          const fontSize = headingData[breakpoint]?.fontSize || 15;
          const fontFamily = activeGlobalStyle.typography.heading.fontFamily;
          editor.mergeCraftHistoryOnce = true;
          setProp((props: Partial<TextProps>) => {
            props.lineHeight = headingData.lineHeight;
            props.backgroundColor = headingData.backgroundColor as Color;
            props.bgSolid = headingData.backgroundColor as RGBA;
            props.appliedGlobalStyle = false;
          });
          const marksStyle: Partial<CustomText> = {
            color: headingData.fontColor as Color,
            textSolid: headingData.fontColor as RGBA,
            fontWeight: headingData.fontWeight,
            letterSpacing: headingData.letterSpacing,
            fontSize: fontSize + activeGlobalStyle.typography.fontSizeUnit,
            fontFamily: fontFamily,
            textDecoration: '',
          };

          applyMarksToAllPreservingOverrides(editor, marksStyle, activeGlobalStyle);

          if (fonts.length > 0) {
            const selected = fonts.find((font) => font.family === fontFamily);
            setSelectedFontFamily(selected);
          }
        }
      } else if (category === 'body') {
        const bodyData = activeGlobalStyle.typography[category as 'body'] as BodyTypography;
        if (bodyData) {
          const bodySize = subVariant as 'small' | 'medium' | 'large';
          const sizeData = bodyData[bodySize];
          editor.mergeCraftHistoryOnce = true;
          setProp((props: Partial<TextProps>) => {
            props.backgroundColor = sizeData.backgroundColor as Color;
            props.bgSolid = sizeData.backgroundColor as RGBA;
            props.lineHeight = sizeData.lineHeight;
            props.appliedGlobalStyle = false;
          });
          const marksStyle: Partial<CustomText> = {
            color: sizeData.fontColor as Color,
            textSolid: sizeData.fontColor as RGBA,
            fontWeight: sizeData.fontWeight,
            letterSpacing: sizeData.letterSpacing,
            fontFamily: bodyData.fontFamily,
            textDecoration: '',
          };
          if (sizeData) {
            marksStyle.fontSize = sizeData.fontSize + activeGlobalStyle.typography.fontSizeUnit;
          }
          applyMarksToAllPreservingOverrides(editor, marksStyle, activeGlobalStyle);

          if (fonts.length > 0) {
            const selected = fonts.find((font) => font.family === bodyData.fontFamily);
            setSelectedFontFamily(selected);
          }
        }
      }
    } else {
      Transforms.unsetNodes(
        editor,
        [
          'fontWeight',
          'fontWeightBeforeBold',
          'fontStyle',
          'bold',
          'italic',
          'color',
          'textSolid',
          'textGradient',
        ],
        {
          at: [],
          match: Text.isText,
        },
      );
      SlateEditor.removeMark(editor, 'fontWeight');
      SlateEditor.removeMark(editor, 'fontWeightBeforeBold');
      SlateEditor.removeMark(editor, 'fontStyle');
      SlateEditor.removeMark(editor, 'bold');
      SlateEditor.removeMark(editor, 'italic');
      SlateEditor.removeMark(editor, 'color');
      SlateEditor.removeMark(editor, 'textSolid');
      SlateEditor.removeMark(editor, 'textGradient');

      setProp((props: Partial<StylingProps>) => {
        props.appliedGlobalStyle = true;
      });
    }
  };

  const handleOnAlignClick = (value: string) => {
    setProp((props: Partial<StylingProps>) => {
      props.textAlign = value as StylingProps['textAlign'];
    }, 500);
  };

  const handleOnLineHeightChange = (value: number) => {
    setProp((props: Partial<StylingProps>) => {
      props.lineHeight = value;
    }, 500);
  };

  const getGlobalFontWeight = () => {
    if (!appliedGlobalStyle || !selectedStyleVariant) return undefined;
    const [category, subVariant] = selectedStyleVariant.split('.') as [
      'heading' | 'body' | 'link',
      string,
    ];

    if (category === 'heading') {
      const headingTag = subVariant as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      return activeGlobalStyle.typography.heading[headingTag]?.fontWeight;
    }

    if (category === 'body') {
      const bodySize = subVariant as 'small' | 'medium' | 'large';
      return activeGlobalStyle.typography.body?.[bodySize]?.fontWeight;
    }

    if (category === 'link') {
      const linkVariant = subVariant as 'link1' | 'link2';
      return activeGlobalStyle.typography.link?.[linkVariant]?.fontWeight;
    }

    return undefined;
  };

  const toggleTextDecoration = (editor: SlateEditorType, style: 'underline' | 'line-through') => {
    const currentDecoration =
      (getCurrentMarkValue(editor, 'textDecoration') as string | undefined) || '';
    const current = currentDecoration.split(' ').filter(Boolean);
    const updated = current.includes(style)
      ? current.filter((item) => item !== style)
      : [...current, style];
    return updated.join(' ') || undefined;
  };

  const handleOnBoldClick = () => {
    const slateEditor = editorRegistry.get(id);
    if (!slateEditor) return;
    const { editor } = slateEditor;
    const fontWeight = getCurrentMarkValue(editor, 'fontWeight') as string | number | undefined;
    const previousWeight = getCurrentMarkValue(editor, 'fontWeightBeforeBold') as
      | string
      | number
      | undefined;
    const globalWeight = getGlobalFontWeight();
    const effectiveWeight = fontWeight ?? globalWeight ?? '400';

    const normalizedWeight =
      effectiveWeight === 'bold'
        ? 700
        : typeof effectiveWeight === 'number'
          ? effectiveWeight
          : effectiveWeight && effectiveWeight !== 'normal'
            ? Number(effectiveWeight)
            : 400;

    const isBold = normalizedWeight >= 700;
    if (isBold) {
      applyMark(editor, 'fontWeightBeforeBold', String(normalizedWeight));
      applyMark(editor, 'fontWeight', '400');
      SlateEditor.removeMark(editor, 'bold');
    } else {
      const previousWeightNumber =
        typeof previousWeight === 'number' ? previousWeight : Number(previousWeight);
      const nextWeight = previousWeightNumber >= 700 ? previousWeightNumber : 700;
      applyMark(editor, 'fontWeight', String(nextWeight));
      SlateEditor.removeMark(editor, 'fontWeightBeforeBold');
      SlateEditor.removeMark(editor, 'bold');
    }
  };

  const handleOnItalicClick = () => {
    const slateEditor = editorRegistry.get(id);
    if (!slateEditor) return;
    const { editor } = slateEditor;
    const currentStyle = getCurrentMarkValue(editor, 'fontStyle');
    const isItalic = currentStyle === 'italic';

    if (isItalic) {
      applyMark(editor, 'fontStyle', 'normal');
    } else {
      applyMark(editor, 'fontStyle', 'italic');
    }
  };

  const handleOnUnderlineClick = () => {
    const slateEditor = editorRegistry.get(id);
    if (!slateEditor) return;
    const { editor } = slateEditor;
    const decoration = toggleTextDecoration(editor, 'underline');
    applyMark(editor, 'textDecoration', decoration);
  };

  const handleOnStrikeThroughClick = () => {
    const slateEditor = editorRegistry.get(id);
    if (!slateEditor) return;
    const { editor } = slateEditor;
    const decoration = toggleTextDecoration(editor, 'line-through');
    applyMark(editor, 'textDecoration', decoration);
  };

  const handleOnAllCapsClick = () => {
    const slateEditor = editorRegistry.get(id);
    if (!slateEditor) return;
    const { editor } = slateEditor;
    const currentTransform = getCurrentMarkValue(editor, 'textTransform');
    const transform = currentTransform === 'uppercase' ? undefined : 'uppercase';
    applyMark(editor, 'textTransform', transform);
  };

  const handleOnLetterSpacingClick = () => {
    const slateEditor = editorRegistry.get(id);
    if (!slateEditor) return;
    const { editor } = slateEditor;
    const currentSpacing = getCurrentMarkValue(editor, 'letterSpacing');
    const spacing = currentSpacing === 0.05 ? undefined : 0.05;
    applyMark(editor, 'letterSpacing', spacing);
  };

  // Get current active states from both marks and props
  const getCurrentFontWeight = () => {
    const slateEditor = editorRegistry.get(id);
    if (!slateEditor) return undefined;
    return getCurrentMarkValue(slateEditor.editor, 'fontWeight');
  };

  const getEffectiveFontWeight = () => {
    const currentWeight = getCurrentFontWeight();
    if (currentWeight !== undefined) {
      return currentWeight;
    }
    return getGlobalFontWeight();
  };

  const getCurrentFontStyle = () => {
    const slateEditor = editorRegistry.get(id);
    if (!slateEditor) return undefined;
    return getCurrentMarkValue(slateEditor.editor, 'fontStyle');
  };

  const effectiveWeight = getEffectiveFontWeight();
  const isBoldActive = effectiveWeight === 'bold' || Number(effectiveWeight) >= 700;

  const getCurrentTextDecoration = () => {
    const slateEditor = editorRegistry.get(id);
    if (!slateEditor) return '';
    return (getCurrentMarkValue(slateEditor.editor, 'textDecoration') as string | undefined) || '';
  };

  const getCurrentTextTransform = () => {
    const slateEditor = editorRegistry.get(id);
    if (!slateEditor) return undefined;
    return getCurrentMarkValue(slateEditor.editor, 'textTransform');
  };

  const getCurrentLetterSpacing = () => {
    const slateEditor = editorRegistry.get(id);
    if (!slateEditor) return undefined;
    return getCurrentMarkValue(slateEditor.editor, 'letterSpacing');
  };

  return (
    <div
      className={clsx(
        'inspector bg-neutral-0 relative z-10 inline-flex h-[calc(100vh-52px)] max-w-[240px] flex-col border-l border-neutral-100',
        className,
      )}
    >
      <InspectorHeader
        className="max-w-[240px] text-neutral-900"
        icon={IconTextSquare}
        text={text}
        showStyle={true}
        applied={appliedGlobalStyle}
        onToggleGlobalStyle={handleOnToggleGlobalStyle}
      />
      <ScrollArea className="h-[calc(100vh-104px)] overflow-hidden">
        <div className="flex flex-col gap-3 p-3">
          <LayoutElementV2
            className=""
            onClick={() => {}}
            state="default"
            data-qa-selector="inspector-text-layout-element"
          />

          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

          <p className="pb-1 text-xs font-semibold text-neutral-900">Text</p>

          <NodeTextAreaSettings data-qa-selector="inspector-text-textarea" />

          {appliedGlobalStyle ? (
            <>
              <NodeGlobalStyleSettings data-qa-selector="inspector-text-global-style" />
              <NodeColorField
                propertyName="color"
                solidPropertyName="textSolid"
                gradientPropertyName="textGradient"
                gradientEnabled
                label="Text Color"
                isSlateMarksMode
                data-qa-selector="inspector-text-text-color-global"
              />
            </>
          ) : (
            <>
              <NodeFontFamilySettings
                onSelect={onHandleChangeFamily}
                data-qa-selector="inspector-text-font-family"
              />
              <NodeFontWeightSettings
                fontFamily={selectedFontFamily}
                data-qa-selector="inspector-text-font-weight"
              />

              <NodeColorField
                propertyName="color"
                solidPropertyName="textSolid"
                gradientPropertyName="textGradient"
                gradientEnabled
                label="Text Color"
                isSlateMarksMode
                data-qa-selector="inspector-text-text-color"
              />

              <NodeColorField
                propertyName="backgroundColor"
                solidPropertyName="bgSolid"
                gradientPropertyName="bgGradient"
                gradientEnabled
                label="Background"
                data-qa-selector="inspector-text-background"
              />

              <NodeTextSizeSettings data-qa-selector="inspector-text-text-size" />
              <NodeSpaceSettings data-qa-selector="inspector-text-space" />

              <SliderField
                className="w-full max-w-[216px]"
                label="Line Height"
                value={lineHeight}
                onChange={handleOnLineHeightChange}
                min={0}
                max={5}
                allowDecimal={true}
                data-qa-selector="inspector-text-line-height"
              />
            </>
          )}
          <AlignField
            onClick={handleOnAlignClick}
            align={textAlign}
            text={'Align'}
            data-qa-selector="inspector-text-align"
          />

          <div className="flex items-center justify-between gap-1">
            <TextButton
              onClick={handleOnBoldClick}
              icon={IconTextBold}
              state={isBoldActive ? 'active' : 'default'}
              dataTestId="text-format-bold"
              data-qa-selector="inspector-text-format-bold"
            />

            <TextButton
              onClick={handleOnItalicClick}
              icon={IconTextItalic}
              state={getCurrentFontStyle() === 'italic' ? 'active' : 'default'}
              dataTestId="text-format-italic"
              data-qa-selector="inspector-text-format-italic"
            />

            <TextButton
              onClick={handleOnUnderlineClick}
              icon={IconTextUnderline}
              state={getCurrentTextDecoration()?.includes('underline') ? 'active' : 'default'}
              dataTestId="text-format-underline"
              data-qa-selector="inspector-text-format-underline"
            />

            <TextButton
              onClick={handleOnStrikeThroughClick}
              icon={IconTextStrikethrough}
              state={getCurrentTextDecoration()?.includes('line-through') ? 'active' : 'default'}
              dataTestId="text-format-strikethrough"
              data-qa-selector="inspector-text-format-strikethrough"
            />

            <TextButton
              onClick={handleOnAllCapsClick}
              icon={IconTextAllCaps}
              state={getCurrentTextTransform() === 'uppercase' ? 'active' : 'default'}
              dataTestId="text-format-all-caps"
              data-qa-selector="inspector-text-format-all-caps"
            />

            <TextButton
              onClick={handleOnLetterSpacingClick}
              icon={IconTextItalicSlash}
              state={getCurrentLetterSpacing() === 0.05 ? 'active' : 'default'}
              dataTestId="text-format-letter-spacing"
              data-qa-selector="inspector-text-format-letter-spacing"
            />
          </div>
          <SecondaryButton
            onClick={handleOnToggleGlobalStyle}
            icon={appliedGlobalStyle ? IconUnlink : IconLink}
            size="md"
            state="default"
            text={appliedGlobalStyle ? 'Detach from Style' : 'Apply Style'}
            data-qa-selector="inspector-text-toggle-style"
          />

          <p className="text-button-sm font-medium text-neutral-400">
            {appliedGlobalStyle
              ? 'Unlink this element from styles to customize it individually.'
              : 'Link element to the latest applied Style.'}
          </p>

          <div className="my-1 h-0 self-stretch outline outline-offset-[-0.50px] outline-neutral-100" />

          <p className="pb-1 text-xs font-semibold text-neutral-900">Interaction</p>

          <Event
            className=""
            selectValue="nolink"
            text="Event"
            data-qa-selector="inspector-text-event"
          />
          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
          <NodeAnimationSettings />
        </div>
      </ScrollArea>
    </div>
  );
};
